// Tabla que muestra los registros de asistencia por ubicacion

import React, { useEffect, useState } from 'react';
import { useUser } from '../UserContext';
import {
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import { format, subDays, addDays, isSameDay } from 'date-fns';
import RegisterAttendanceCard from './RegisterAttendanceCard';
import { apiFetchJson } from '../fetchWrapper';
import { getAttendanceStatusLabel } from './AttendanceStatus';
import { getShiftDateTime, getShiftAttendance } from '../util/ShiftUtil';


export default function RegisterAttendance() {
  const { user, setUser, hasRole } = useUser();
  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [scheduledShifts, setScheduledShifts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendances, setAttendances] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [myAttendances, setMyAttendances] = useState([]);
  const [directPunchSiteId, setDirectPunchSiteId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentHour, setCurrentHour] = useState(new Date().getHours());
  const [now, setNow] = useState(new Date());

  const openDirectAttendance = selectedEmployeeId
    ? attendances.find(a => a.employeeId === Number(selectedEmployeeId) && !a.scheduledShiftId && a.checkIn && !a.checkOut)
    : null;

  const mergeAttendanceUpdate = (updatedAttendance) => {
    setAttendances(prev => {
      const exists = prev.some(a => a.id === updatedAttendance.id);
      return exists ? prev.map(a => a.id === updatedAttendance.id ? updatedAttendance : a) : [...prev, updatedAttendance];
    });
    setMyAttendances(prev => {
      const isMyAttendance = updatedAttendance.employeeId === user?.id;
      const exists = prev.some(a => a.id === updatedAttendance.id);
      if (exists || isMyAttendance) {
        return exists ? prev.map(a => a.id === updatedAttendance.id ? updatedAttendance : a) : [...prev, updatedAttendance];
      }
      return prev;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
        const previousDate = subDays(selectedDate, 1);
        const previousDateStr = format(previousDate, 'yyyy-MM-dd');
        const weekAgoDate = subDays(selectedDate, 7);
        const weekAgoDateStr = format(weekAgoDate, 'yyyy-MM-dd');

        const [sitesRes, employeesRes, templatesRes, shiftsRes, attendancesRes, myShiftsRes, myAttendancesRes] = await Promise.all([
          apiFetchJson('/api/sites'),
          apiFetchJson('/api/employees'),
          apiFetchJson('/api/shift-templates'),
          apiFetchJson(`/api/scheduled-shifts?startDate=${previousDateStr}&endDate=${selectedDateStr}`),
          apiFetchJson(`/api/attendances?startDate=${previousDateStr}&endDate=${selectedDateStr}`),
          apiFetchJson(`/api/scheduled-shifts?startDate=${weekAgoDateStr}&employeeId=${user.id}`),
          apiFetchJson(`/api/attendances?startDate=${weekAgoDateStr}&employeeId=${user.id}`)
        ]);
        setSites(Array.isArray(sitesRes) ? sitesRes : []);
        setEmployees(Array.isArray(employeesRes) ? employeesRes : []);
        setShiftTemplates(Array.isArray(templatesRes) ? templatesRes : []);
        setScheduledShifts(Array.isArray(shiftsRes) ? shiftsRes : []);
        setAttendances(Array.isArray(attendancesRes) ? attendancesRes : []);
        setMyShifts(Array.isArray(myShiftsRes) ? myShiftsRes : []);
        setMyAttendances(Array.isArray(myAttendancesRes) ? myAttendancesRes : []);
      } catch (err) {
        console.error('Failed to fetch initial data', err);
        setError(err.message || 'Failed to fetch data');
      }
    };

    fetchData();
  }, [selectedDate, user?.id]);

  useEffect(() => {
    if (!selectedEmployeeId) {
      setScheduledShifts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    apiFetchJson(`/api/scheduled-shifts?employeeId=${selectedEmployeeId}`).then(shifts => {
      setScheduledShifts(shifts);
      setLoading(false);
    }).catch(err => {
      console.error('Failed to fetch scheduled shifts', err);
      setLoading(false);
    });
  }, [selectedEmployeeId]);

  const shiftsWithTime = Array.isArray(myShifts)
    ? myShifts
        .map(s => {
          const times = getShiftDateTime(s, shiftTemplates);
          return times ? { ...s, ...times } : null;
        })
        .filter(Boolean)
    : [];

  const setNextShiftAttendance = (updatedAttendance) => {
    if (!updatedAttendance || !updatedAttendance.id) {
      console.warn("Invalid attendance object:", updatedAttendance);
      return;
    }
    setAttendances(prev => {
      const exists = prev.some(a => a.id === updatedAttendance.id);

      if (exists) {
        return prev.map(a =>
          a.id === updatedAttendance.id ? updatedAttendance : a
        );
      } else {
        return [...prev, updatedAttendance];
      }
    });
  };

  const currentShift = shiftsWithTime.find(
    s => s.start <= now && s.end >= now
  ) || null;

  const nextShift = shiftsWithTime
    .filter(s => s.start > now)
    .sort((a, b) => a.start - b.start)[0] || null;

  const lastShift = shiftsWithTime
    .filter(s => s.end < now)
    .sort((a, b) => b.start - a.start)[0] || null;

  const activeShift = currentShift || nextShift || lastShift;
  // const activeShiftAttendance = activeShift ? myAttendances.find(a => a.scheduledShiftId === activeShift.id) : null;
  const activeShiftAttendance = getShiftAttendance(activeShift, myAttendances);
  const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId) || null;
  const allowManualAttendance = hasRole(['ADMIN', 'HR_ADMIN']);
  const manualAttendance = allowManualAttendance && selectedEmployeeId && !activeShift;

  const handleChange = (e) => {
    setSelectedEmployeeId(e.target.value);
  };

  const getSite = (siteId) => {
    return sites.find(s => s.id === siteId) || siteId;
  };

  useEffect(() => {
    if (!sites.length) return;
    const mainOffice = sites.find(s => s.name === 'Gosen Oficina');
    setDirectPunchSiteId(mainOffice?.id || sites[0]?.id || '');
  }, [sites]);

  const handleDirectCheckIn = () => {
    if (!selectedEmployeeId) {
      alert('Selecciona un empleado antes de registrar asistencia directa.');
      return;
    }

    if (!directPunchSiteId) {
      alert('Selecciona una ubicación para la asistencia directa.');
      return;
    }

    if (!navigator.geolocation) {
      alert('Geolocalización no soportada en este navegador');
      return;
    }

    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      const payload = {
        employeeId: Number(selectedEmployeeId),
        siteId: Number(directPunchSiteId),
        attendanceStatus: 'ON_TIME',
        latitude,
        longitude,
        checkIn: new Date().toISOString(),
      };

      await apiFetchJson('/api/attendances', {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((res) => {
        if (!res) {
          throw new Error('No se pudo registrar la asistencia directa');
        }
        mergeAttendanceUpdate(res);
        alert('Asistencia directa registrada correctamente');
      }).catch((err) => {
        console.error('Error al registrar asistencia directa:', err);
        alert('No se pudo registrar la asistencia directa.');
      });
    });
  };

  const handleDirectCheckOut = async () => {
    if (!openDirectAttendance) {
      alert('No existe un registro directo abierto para este empleado.');
      return;
    }

    const payload = {
      ...openDirectAttendance,
      checkOut: new Date().toISOString(),
    };

    await apiFetchJson(`/api/attendances/${openDirectAttendance.id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }).then((res) => {
      if (!res) {
        throw new Error('No se pudo registrar la salida directa');
      }
      mergeAttendanceUpdate(res);
      alert('Salida directa registrada correctamente');
    }).catch((err) => {
      console.error('Error al registrar la salida directa:', err);
      alert('No se pudo registrar la salida directa.');
    });
  };

  const getTemplate = (templateId) => {
    return shiftTemplates.find(t => t.id === templateId) || templateId;
  };


  const hours = Array.from({ length: 24 }, (_, i) => i); // 0..23

  // Update current hour and now every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHour(new Date().getHours());
      setNow(new Date());
    }, 60000); // every minute
    return () => clearInterval(interval);
  }, []);

  function getHourCellShiftData({ empId, hour, selectedDate, currentHour, scheduledShifts, attendances, getTemplate }) {
    // Find any shift that is active in this hour
    const shift = scheduledShifts.find((shift) => {
      if (shift.employeeId !== empId) return false;
      const template = getTemplate(shift.shiftTemplateId);
      if (!template) return false;

      const startHour = parseInt(template.startTime.split(":")[0], 10);
      const endHour = parseInt(template.endTime.split(":")[0], 10);
      const shiftDate = new Date(shift.shiftDate);

      const prevDay = new Date(selectedDate);
      prevDay.setDate(prevDay.getDate() - 1);

      if (startHour < endHour) {
        return isSameDay(shiftDate, selectedDate) && hour >= startHour && hour < endHour;
      } else {
        return (isSameDay(shiftDate, selectedDate) && hour >= startHour) ||
          (isSameDay(shiftDate, prevDay) && hour < endHour);
      }
    });

    // Default colors
    let bgColor = "white";
    let color = "inherit";
    let status = "NO_SHOW";
    let site = {};

    if (shift) {
      site = getSite(shift.siteId);
      const attendance = Array.isArray(attendances) ? attendances.find(a => a.scheduledShiftId == shift.id) : null;
      if (!attendance) {
        bgColor = "gray";
        color = "white";
      } else {
        status = attendance.attendanceStatus;
        switch (attendance.attendanceStatus) {
          case "ON_TIME":
            bgColor = "green";
            color = "white";
            break;
          case "LATE":
            bgColor = "orange";
            color = "white";
            break;
          case "LEFT_EARLY":
            bgColor = "yellow";
            color = "black";
            break;
          case "NO_SHOW":
          case "ABSENT":
            bgColor = "red";
            color = "white";
            break;
          case "OVERTIME":
            bgColor = "blue";
            color = "white";
            break;
          case "SCHEDULED":
            bgColor = "#90caf9";
            color = "black";
            break;
          default:
            bgColor = "gray";
            color = "white";
        }
      }
    } else if (hour === currentHour && isSameDay(selectedDate, new Date())) {
      bgColor = "rgba(0,128,0,0.1)";
    }

    return { bgColor, color, status, site };
  }

  const groupedByDate = Object.values(
    scheduledShifts.reduce((acc, shift) => {
      const date = new Date(shift.shiftDate).toLocaleDateString();

      if (!acc[date]) {
        acc[date] = [];
      }

      acc[date].push(shift);
      return acc;
    }, {})
  ).flat();

  return (

    <Box>

      <RegisterAttendanceCard
        nextShift={activeShift}
        nextShiftAttendance={activeShiftAttendance}
        setNextShiftAttendance={setNextShiftAttendance}
        selectedEmployee={selectedEmployee}
        manualAttendance={manualAttendance}
        allowManualAttendance={allowManualAttendance}
      />

      <Paper sx={{
        width: '100%',
        p: 3,
        mt: 3,
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
      }}
      >

        <Typography variant="h6" color="primary"> Periódo</Typography>

        <Box sx={{ width: '100%', border: '3px solid #eaeaea', p: 1 }}>
          <Box sx={{
            mt: 2,
            mb: 2,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 1
          }}>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setSelectedDate(prev => subDays(prev, 1))}>
              Día Anterior
            </Button>

            <Typography variant="subtitle1" sx={{ minWidth: 120, textAlign: 'center' }}>
              {format(selectedDate, 'yyyy-MM-dd')}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setSelectedDate(prev => addDays(prev, 1))}>
              Día Siguiente
            </Button>
          </Box>

          <TableContainer sx={{ width: '100%' }}>
            <Table
              sx={{
                borderTop: '3px solid #eaeaea',
                tableLayout: 'fixed',
                width: '100%',
                borderCollapse: 'collapse',

                '& .MuiTableCell-root': {
                  height: 32,
                  minHeight: 32,
                  maxHeight: 32,
                  p: 0,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  borderRight: '1px solid #eaeaea',
                },

                '& .MuiTableCell-root:last-child': {
                  borderRight: 'none',
                },

                '& .MuiTableCell-head': {
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ height: 32 }}>
                  <TableCell sx={{
                    width: 120,
                    minWidth: 120,
                    maxWidth: 120,
                  }}
                  >
                    Nombre / Hrs
                  </TableCell>

                  {hours.map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        textAlign: 'center',
                        backgroundColor: h === currentHour ? "rgba(0,128,0,0.1)" : "white",
                      }}
                    >{h}
                    </TableCell>
                  ))}

                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((emp) => (
                  <TableRow key={emp.id}>

                    <TableCell sx={{ p: 0 }}>
                      <Box
                        sx={{
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'left',
                          overflow: 'hidden',
                        }}
                      >
                        {emp.user.person.firstname} {emp.user.person.lastnameFirst}
                      </Box>
                    </TableCell>

                    {hours.map((h) => {
                      const { bgColor, color, status, site } = getHourCellShiftData({
                        empId: emp.id,
                        hour: h,
                        selectedDate,
                        currentHour,
                        scheduledShifts,
                        attendances,
                        getTemplate
                      });

                      return (
                        <TableCell
                          key={h}
                          sx={{
                            backgroundColor: bgColor,
                            color,
                            height: 32,
                            p: 0,
                          }}
                        >
                          <Tooltip title={`${site.name} - ${getAttendanceStatusLabel(status)} - ${h}:00`} arrow >
                            <Box sx={{
                              height: 32,
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }} />
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <FormControl size="small" sx={{ minWidth: 250, paddingBottom: 2 }}>
        <InputLabel id="employee-select-label">Selecciona un empleado</InputLabel>
        <Select
          labelId="employee-select-label"
          id="employeeSelect"
          value={selectedEmployeeId}
          label="Selecciona un empleado"
          onChange={handleChange}
        >
          <MenuItem value="">
            <em>Ninguno</em>
          </MenuItem>
          {employees.map((emp) => (
            <MenuItem key={emp.id} value={emp.id}>
              {emp.user.person.firstname} {emp.user.person.lastnameFirst}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {hasRole(['ADMIN', 'HR_ADMIN']) && selectedEmployeeId && (
        <Paper sx={{ p: 2, mb: 2, backgroundColor: '#eef7ff' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Registrar asistencia directa
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="direct-site-select-label">Ubicación</InputLabel>
            <Select
              labelId="direct-site-select-label"
              id="directSiteSelect"
              value={directPunchSiteId}
              label="Ubicación"
              onChange={(e) => setDirectPunchSiteId(e.target.value)}
            >
              {sites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {openDirectAttendance ? (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
              <Typography variant="body2">
                Registro directo abierto para el empleado seleccionado.
              </Typography>
              <Typography variant="body2">
                Entrada: {new Date(openDirectAttendance.checkIn).toLocaleString()}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={handleDirectCheckOut}
                sx={{ mt: 1 }}
              >
                Registrar salida directa
              </Button>
            </Box>
          ) : (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleDirectCheckIn}
            >
              Registrar entrada directa
            </Button>
          )}
        </Paper>
      )}

      {scheduledShifts.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead
              sx={{
                backgroundColor: '#f5f5f5',
                textTransform: 'uppercase',
              }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Ubicación</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Turno</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>CheckIn</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>CheckOut</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {groupedByDate.map((shift, index, arr) => {
                const currentDate = new Date(shift.shiftDate).toLocaleDateString();
                const prevDate =
                  index > 0
                    ? new Date(arr[index - 1].shiftDate).toLocaleDateString()
                    : null;

                const isNewGroup = currentDate !== prevDate;
                const attendance = getShiftAttendance(shift, attendances);
                //const site = getSite(shift.siteId)?.name || shift.siteId;
                const site = getSite(shift.siteId);
                const siteName = site?.client.companyName + " - " + site?.name || shift.siteId;
                return (
                  <TableRow key={shift.id}
                    sx={{
                      backgroundColor: isNewGroup ? "#d0d0d0" : "white",
                    }}
                  >
                    <TableCell>{new Date(shift.shiftDate).toLocaleDateString()}</TableCell>
                    <TableCell>{siteName}</TableCell>
                    <TableCell>{getTemplate(shift.shiftTemplateId)?.name || shift.shiftTemplateId}</TableCell>
                    <TableCell>
                      {attendance && attendance.checkIn ? (
                        new Date(attendance.checkIn).toLocaleTimeString()
                      ) : (
                        // Here you can show a message indicating that checkIn is not available
                        "N/A" 
                      )}
                    </TableCell>
                    <TableCell>
                      {attendance && attendance.checkOut ? (
                        new Date(attendance.checkOut).toLocaleTimeString()
                      ) : (
                        // Here you can show a message indicating that checkOut is not available
                       "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        selectedEmployeeId && <p>No se encontraron turnos programados para este empleado.</p>
      )}

    </Box>
  );
}